import { useTranslation } from "react-i18next";
import {
  DocumentCraftingFindingSeverity,
  DocumentCraftingIngestConfidence,
  DocumentCraftingLabelColor,
  DocumentCraftingLabelScope,
  DocumentCraftingLanguage,
  DocumentCraftingPermission,
  DocumentCraftingRevisionOrigin,
  DocumentCraftingRevisionStatus,
  DocumentCraftingRunErrorCode,
  DocumentCraftingRunKind,
  DocumentCraftingRunStatus,
  DocumentCraftingTranslationStatus,
  DocumentCraftingType,
  DocumentCraftingTypeKey,
  DocumentCraftingUploadKind,
  DocumentCraftingVisibility,
  LocalizedText,
} from "../../../Models/documentCrafting";

export { enumValues } from "../../Ambassador/Common/useAmbassadorLabels";

export const TYPE_KEY_BY_ID: Record<
  DocumentCraftingType,
  DocumentCraftingTypeKey
> = {
  [DocumentCraftingType.PRESENTATION]: "presentation",
  [DocumentCraftingType.ONEPAGER_PARTNER]: "onepager_partner",
  [DocumentCraftingType.ONEPAGER_SERVICE]: "onepager_service",
  [DocumentCraftingType.FLYER]: "flyer",
  [DocumentCraftingType.CASE_STUDY]: "case_study",
  [DocumentCraftingType.INVITATION]: "invitation",
  [DocumentCraftingType.CHECKLIST_GUIDE]: "checklist_guide",
  [DocumentCraftingType.SOCIAL_POST]: "social_post",
  [DocumentCraftingType.DIAGRAM]: "diagram",
};

/** The language of a localised API text that matches the UI ("de" / "en"). */
export const uiLanguage = (language: string): DocumentCraftingLanguage =>
  language.toLowerCase().startsWith("de") ? "de" : "en";

/** Picks the UI language from `{en, de}` texts of the API (form schema). */
export const localizedText = (
  text: LocalizedText | null | undefined,
  language: string,
) => {
  if (!text) return "";
  return uiLanguage(language) === "de"
    ? text.de || text.en
    : text.en || text.de;
};

/** Translated labels of the module's enumerations and fixed strings. */
export const useDocumentCraftingEnumLabels = () => {
  const { t, i18n } = useTranslation("documentCrafting");

  return {
    type: (key: DocumentCraftingTypeKey) => t(`enums.types.${key}.title`),
    typeDescription: (key: DocumentCraftingTypeKey) =>
      t(`enums.types.${key}.description`),
    typeById: (type: DocumentCraftingType) =>
      t(`enums.types.${TYPE_KEY_BY_ID[type]}.title`),
    runStatus: (value: DocumentCraftingRunStatus) =>
      t(`enums.runStatus.${value}`),
    runKind: (value: DocumentCraftingRunKind) => t(`enums.runKind.${value}`),
    revisionStatus: (value: DocumentCraftingRevisionStatus) =>
      t(`enums.revisionStatus.${value}`),
    origin: (value: DocumentCraftingRevisionOrigin) =>
      t(`enums.origin.${value}`),
    visibility: (value: DocumentCraftingVisibility) =>
      t(`enums.visibility.${value}`),
    permission: (value: DocumentCraftingPermission) =>
      t(`enums.permission.${value}`),
    labelScope: (value: DocumentCraftingLabelScope) =>
      t(`enums.labelScope.${value}`),
    labelColor: (value: DocumentCraftingLabelColor) =>
      t(`enums.labelColors.${value}`),
    language: (value: DocumentCraftingLanguage) =>
      t(`enums.languages.${value}`),
    uploadKind: (value: DocumentCraftingUploadKind) =>
      t(`enums.uploadKind.${value}`),
    ingestConfidence: (value: DocumentCraftingIngestConfidence) =>
      t(`enums.ingestConfidence.${value}`),
    translationStatus: (value: DocumentCraftingTranslationStatus) =>
      t(`enums.translationStatus.${value}`),
    severity: (value: DocumentCraftingFindingSeverity) =>
      t(`enums.severity.${value}`),
    /** Readable reason of a failed run; falls back to the server text. */
    errorCode: (
      value: DocumentCraftingRunErrorCode | null,
      fallback?: string | null,
    ) =>
      value
        ? t(`enums.errorCodes.${value}`, { defaultValue: fallback ?? "" })
        : (fallback ?? t("enums.errorCodes.internal")),
    localized: (text: LocalizedText | null | undefined) =>
      localizedText(text, i18n.language),
  };
};

export type DocumentCraftingEnumLabels = ReturnType<
  typeof useDocumentCraftingEnumLabels
>;
